import React, { useState, useEffect } from "react";
import { useQuery, useAction } from "wasp/client/operations";
import { getTextbookById, updateTextbook, deleteTextbook } from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import { useNavigate, useParams } from "react-router-dom";
import {
  IndividualEditPageLayout,
  SidebarSection,
  ActionButtons,
  ActionButton,
  ActionButtonSecondary,
} from "../../components/layout";
import { useLocationContext } from "../../client/contexts/LocationContext";
import {
  BookOpen,
  DollarSign,
  Phone,
  Mail,
  Save,
  Tag,
  Hash,
  GraduationCap,
  Trash2,
  AlertCircle,
} from "lucide-react";
import { useRequireSectionVisible } from "../../client/hooks/useRequireSectionVisible";
import { AudienceSelect, toAudience, type Audience } from "../../client/components/AudienceSelect";

interface TextbookFormData {
  title: string;
  author: string;
  isbn: string;
  course: string;
  description: string;
  price: string;
  category: "BUY" | "SELL" | "BORROW";
  condition: "NEW" | "USED";
  contactPhone: string;
  contactEmail: string;
}

export default function EditTextbookPage() {
  useRequireSectionVisible("showBuySell");
  const { data: user } = useAuth();
  const { selectedLocation } = useLocationContext();
  const navigate = useNavigate();
  const { itemId } = useParams<{ itemId: string }>();

  const { data: textbook, isLoading } = useQuery(getTextbookById, { itemId: itemId! });
  const updateTextbookFn = useAction(updateTextbook);
  const deleteTextbookFn = useAction(deleteTextbook);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [audience, setAudience] = useState<Audience>("VERIFIED");
  
  const [formData, setFormData] = useState<TextbookFormData>({
    title: "",
    author: "",
    isbn: "",
    course: "",
    description: "",
    price: "",
    category: "SELL",
    condition: "USED",
    contactPhone: "",
    contactEmail: "",
  });

  useEffect(() => {
    if (textbook) {
      const meta = textbook.metadata as any;
      setFormData({
        title: textbook.title || "",
        author: meta?.author || "",
        isbn: meta?.isbn || "",
        course: meta?.course || "",
        description: textbook.description || "",
        price: meta?.price ? meta.price.toString() : "0",
        category: meta?.category || "SELL",
        condition: meta?.condition || "USED",
        contactEmail: meta?.contactEmail || (meta?.contactInfo?.includes("@") ? meta.contactInfo.split("|")[0].trim() : ""),
        contactPhone: meta?.contactPhone || (meta?.contactInfo?.includes("|") ? meta.contactInfo.split("|")[1].trim() : ""),
      });
      setAudience(toAudience(textbook.visibility));
    }
  }, [textbook]);

  const formatPhoneNumber = (value: string) => {
    const numbers = value?.replace(/\D/g, "");
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 6) return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
    return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
  };

  const handleChange = (field: keyof TextbookFormData, value: any) => {
    if (field === "contactPhone") {
      value = formatPhoneNumber(value);
    }
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.title.trim()) newErrors.title = "Book title is required";
    if (!formData.description.trim()) newErrors.description = "Description is required";
    if (!formData.price.trim()) newErrors.price = "Price is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      await updateTextbookFn({
        itemId: itemId!,
        title: formData.title.trim(),
        description: formData.description.trim(),
        price: parseFloat(formData.price) || 0,
        category: formData.category,
        condition: formData.condition,
        contactInfo: `${formData.contactEmail}${formData.contactPhone ? ` | ${formData.contactPhone}` : ""}`,
        visibility: audience,
        metadata: {
          author: formData.author.trim(),
          isbn: formData.isbn.trim(),
          course: formData.course.trim(),
        }
      });
      handleGoBack();
    } catch (error) {
      console.error("Error updating textbook:", error);
      setErrors({ submit: "Failed to update textbook." });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this textbook?")) return;
    try {
      await deleteTextbookFn({ itemId: itemId! });
      handleGoBack();
    } catch (error) {
      console.error("Error deleting textbook:", error);
    }
  };

  const handleGoBack = () => {
    if (selectedLocation?.locationCode) {
      navigate(`/locations/${selectedLocation.locationCode}/textbooks`);
    } else {
      navigate("/textbooks");
    }
  };

  if (!user) {
    return (
      <IndividualEditPageLayout title="Access Denied" icon={AlertCircle}>
        <div className="p-6 text-center py-12">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Access Denied
          </h3>
          <p className="text-gray-600">You must be logged in to edit textbooks.</p>
        </div>
      </IndividualEditPageLayout>
    );
  }

  if (isLoading) {
    return (
      <IndividualEditPageLayout title="Loading Textbook...">
        <div className="p-6 text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading your textbook listing...</p>
        </div>
      </IndividualEditPageLayout>
    );
  }

  if (!textbook) {
    return (
      <IndividualEditPageLayout title="Textbook Not Found" icon={AlertCircle}>
        <div className="p-6 text-center py-12">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Textbook Not Found
          </h3>
          <p className="text-gray-600 mb-4">
            The textbook listing you're looking for doesn't exist.
          </p>
          <button
            onClick={handleGoBack}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </IndividualEditPageLayout>
    );
  }

  const actionsContent = (
    <ActionButtons>
      <ActionButton onClick={handleSubmit} disabled={isSubmitting} icon={Save}>
        {isSubmitting ? "Updating..." : "Update Item"}
      </ActionButton>
      <ActionButtonSecondary onClick={handleGoBack}>Cancel</ActionButtonSecondary>
    </ActionButtons>
  );

  const sidebarContent = (
    <>
      <SidebarSection title="Post Type" hideOnMobile>
        <div className="flex bg-slate-100 p-1 rounded-xl w-full">
          <button
            type="button"
            onClick={() => handleChange("category", "SELL")}
            className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all ${
              formData.category === "SELL" ? "bg-blue-600 text-white shadow-sm" : "text-slate-500 hover:text-slate-700"
            }`}
          >
            Selling
          </button>
          <button
            type="button"
            onClick={() => handleChange("category", "BUY")}
            className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all ${
              formData.category === "BUY" ? "bg-blue-600 text-white shadow-sm" : "text-slate-500 hover:text-slate-700"
            }`}
          >
            Buying
          </button>
        </div>
      </SidebarSection>
      <SidebarSection title="Condition" hideOnMobile>
        <div className="flex bg-slate-100 p-1 rounded-xl w-full">
          <button
            type="button"
            onClick={() => handleChange("condition", "NEW")}
            className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all ${
              formData.condition === "NEW" ? "bg-slate-700 text-white shadow-sm" : "text-slate-500 hover:text-slate-700"
            }`}
          >
            New
          </button>
          <button
            type="button"
            onClick={() => handleChange("condition", "USED")}
            className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all ${
              formData.condition === "USED" ? "bg-slate-700 text-white shadow-sm" : "text-slate-500 hover:text-slate-700"
            }`}
          >
            Used
          </button>
        </div>
      </SidebarSection>
      <SidebarSection title="Danger Zone">
        <button
          onClick={handleDelete}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 text-red-600 bg-red-50 border border-red-100 rounded-xl hover:bg-red-100 transition-colors font-semibold"
        >
          <Trash2 className="w-4 h-4" /> Delete Item
        </button>
      </SidebarSection>
    </>
  );

  return (
    <IndividualEditPageLayout
      title="Edit Textbook"
      subtitle={`Administering your copy of ${formData.title}`}
      icon={BookOpen}
      actions={actionsContent}
      sidebar={sidebarContent}
    >
      <form id="textbook-form" onSubmit={handleSubmit} className="space-y-8">
        <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-slate-900 mb-2 uppercase tracking-tight">Book Title</label>
              <div className="relative">
                <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleChange("title", e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-medium"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-900 mb-2 uppercase tracking-tight">Author</label>
              <input
                type="text"
                value={formData.author}
                onChange={(e) => handleChange("author", e.target.value)}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-medium"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-semibold text-slate-900 mb-2 uppercase tracking-tight">ISBN (Optional)</label>
              <div className="relative">
                <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={formData.isbn}
                  onChange={(e) => handleChange("isbn", e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-medium"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-900 mb-2 uppercase tracking-tight">Course Code (Optional)</label>
              <div className="relative">
                <GraduationCap className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={formData.course}
                  onChange={(e) => handleChange("course", e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-medium"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-900 mb-2 uppercase tracking-tight">Price ($)</label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="number"
                  value={formData.price}
                  onChange={(e) => handleChange("price", e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-medium"
                />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-900 mb-2 uppercase tracking-tight">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              rows={4}
              className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-medium"
            />
          </div>

          <AudienceSelect value={audience} onChange={setAudience} />
        </div>

        <div className="space-y-4">
          <h3 className="font-bold text-slate-900 flex items-center gap-2 px-1"><Mail className="w-4 h-4 text-blue-600" /> Contact Info</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="email"
                value={formData.contactEmail}
                readOnly
                className="w-full pl-10 pr-4 py-3 bg-slate-100 border border-slate-200 rounded-xl text-slate-500 cursor-not-allowed"
              />
            </div>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={formData.contactPhone}
                onChange={(e) => handleChange("contactPhone", e.target.value)}
                placeholder="(613) XXX-XXXX"
                className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:bg-white focus:ring-2 focus:ring-blue-500/20 transition-all font-medium"
              />
            </div>
          </div>
        </div>
        {errors.submit && <div className="p-4 bg-red-50 border border-red-100 rounded-xl text-red-700 text-sm font-medium">{errors.submit}</div>}
      </form>
    </IndividualEditPageLayout>
  );
}
